import { defineStore } from 'pinia';
import { ref } from 'vue';

export type AlertType = 'success' | 'info' | 'warning' | 'error' | 'progress';

/**
 * Visual style variant for alert buttons.
 * - 'primary'   — filled accent button (e.g. "Continue")
 * - 'secondary' — outlined / subdued button (e.g. "Cancel")
 * - 'danger'    — filled destructive button (e.g. "Delete")
 * - 'ghost'     — text-only, minimal chrome (e.g. "Ignore")
 */
export type AlertButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

/**
 * Definition for a single button rendered inside an alert banner.
 */
export interface AlertButton {
  /** Unique key returned when this button is clicked */
  value: string;
  /** Human-readable label shown on the button */
  label: string;
  /** Visual variant – defaults to 'secondary' if omitted */
  variant?: AlertButtonVariant;
}

/**
 * The value resolved back to the caller when the user interacts with
 * a button-bearing alert.
 */
export interface AlertButtonResponse {
  /** The `value` of the button the user clicked */
  action: string;
  /** The alert ID that was resolved */
  alertId: string;
}

export interface Alert {
  id: string;
  type: AlertType;
  content: string;
  keepAlive: boolean;
  createdAt: number;
  duration: number; // in milliseconds
  progress: number; // from 0 to 100
  /** Optional interactive buttons – when present the alert persists until a button is clicked */
  buttons?: AlertButton[];
}

export const useAlertStore = defineStore('alert', () => {
  const alerts = ref<Alert[]>([]);

  /**
   * Map of alert-id → Promise resolve function.
   * Used internally to settle the promise returned by `showAlertWithButtons`.
   */
  const pendingResolvers = new Map<
    string,
    (response: AlertButtonResponse) => void
  >();

  /**
   * Hide a specific alert by ID
   * @param id The ID of the alert to hide
   */
  function hideAlert(id: string) {
    const index = alerts.value.findIndex(alert => alert.id === id);
    if (index !== -1) {
      alerts.value.splice(index, 1);
    }
    // Clean up any pending resolver (treat as dismissed / no response)
    pendingResolvers.delete(id);
  }

  function isUniqueAlert(content: string): boolean {
    return !alerts.value.some(alert => alert.content === content);
  }

  function getIndenticalID(content: string): string | null {
    const existingAlert = alerts.value.find(
      alert => alert.content === content,
    );
    return existingAlert ? existingAlert.id : null;
  }

  /**
   * Show an alert with the specified type and content
   * @param type The type of alert: 'success', 'info', 'warning', or 'error'
   * @param content The message to display
   * @param keepAlive If true, the alert will not automatically dismiss
   * @param duration Duration in milliseconds before the alert disappears (if not keepAlive)
   * @returns The ID of the created alert
   */
  function showAlert(
    type: AlertType,
    content: string,
    keepAlive = false,
    duration = 10000,
  ): string {
    // Prevent showing duplicate alerts with the same content
    if (!isUniqueAlert(content)) {
      return getIndenticalID(content) || '';
    }

    const id = `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const alert: Alert = {
      id,
      type,
      content: `${content}`,
      keepAlive,
      createdAt: Date.now(),
      duration,
      progress: 100,
    };

    // Add to the beginning of the array so newer alerts are at the top
    alerts.value.push(alert);

    if (!keepAlive) {
      // Start the progress countdown
      let elapsed = 0;
      const interval = 50; // Update every 50ms for smooth animation

      const timer = setInterval(() => {
        const alertIndex = alerts.value.findIndex(a => a.id === id);
        if (alertIndex === -1) {
          clearInterval(timer);
          return;
        }

        elapsed += interval;
        const computedProgress = Math.max(0, 100 - (elapsed / duration) * 100);

        // Update the progress
        alerts.value[alertIndex].progress = computedProgress;

        if (elapsed >= duration) {
          clearInterval(timer);
          hideAlert(id);
        }
      }, interval);
    }

    return id;
  }

  /**
   * Show a persistent alert with interactive buttons and return a Promise
   * that resolves when the user clicks one of the buttons.
   *
   * @param type   The visual type of the alert
   * @param content  The message to display
   * @param buttons  Array of button definitions
   * @returns A Promise that resolves with the user's button choice
   *
   * @example
   * ```ts
   * const { action } = await alertStore.showAlertWithButtons(
   *   'warning',
   *   'Unsaved changes will be lost.',
   *   [
   *     { value: 'cancel', label: 'Cancel', variant: 'secondary' },
   *     { value: 'ignore', label: 'Ignore', variant: 'ghost' },
   *     { value: 'continue', label: 'Continue', variant: 'primary' },
   *   ]
   * );
   * ```
   */
  async function showAlertWithButtons(
    type: AlertType,
    content: string,
    buttons: AlertButton[],
  ): Promise<AlertButtonResponse> {
    // Prevent showing duplicate button alerts with the same content
    if (!isUniqueAlert(content)) {
      return { action: 'cancel', alertId: '' };
    }

    const id = `alert-btn-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    const alert: Alert = {
      id,
      type,
      content,
      keepAlive: true, // Button alerts always persist until a button is clicked
      createdAt: Date.now(),
      duration: 0,
      progress: 100,
      buttons,
    };

    alerts.value.push(alert);

    return new Promise<AlertButtonResponse>((resolve) => {
      pendingResolvers.set(id, resolve);
    });
  }

  /**
   * Called when a user clicks a button on an interactive alert.
   * Resolves the pending promise and removes the alert.
   *
   * @param id     The ID of the alert
   * @param action The `value` of the clicked button
   */
  function resolveAlert(id: string, action: string) {
    const resolver = pendingResolvers.get(id);
    if (resolver) {
      resolver({ action, alertId: id });
      pendingResolvers.delete(id);
    }
    hideAlert(id);
  }

  /**
   * Modify a specific alert by ID
   * @param id The ID of the alert to modify
   * @param content The new content to set for the alert
   */
  function updateAlert(id: string | null, content: string) {
    // If id is null, jsut create a generic new alert:
    if (id === null) {
      showAlert('info', content, false, 10000);
      return;
    }

    const index = alerts.value.findIndex(alert => alert.id === id);
    if (index !== -1) {
      alerts.value[index].content = content;
    }
  }

  /**
   * Hide all currently displayed alerts
   */
  function hideAllAlerts() {
    alerts.value = [];
    pendingResolvers.clear();
  }

  /**
   * Show or update a progress alert
   * @param message The message to display
   * @param progressPercent The progress percentage (0-100)
   * @param id Optional ID for updating existing progress alert
   * @returns The ID of the created/updated alert
   */
  function progress(
    message: string,
    progressPercent: number,
    id?: string,
  ): string {
    // Clamp progress between 0 and 100
    const clampedProgress = Math.max(0, Math.min(100, progressPercent));

    // If ID is provided, try to update existing alert
    if (id) {
      const index = alerts.value.findIndex(alert => alert.id === id);
      if (index !== -1) {
        alerts.value[index].content = message;
        alerts.value[index].progress = clampedProgress;
        return id;
      }
    }

    // Create new progress alert
    const alertId
      = id || `progress-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const alert: Alert = {
      id: alertId,
      type: 'progress',
      content: message,
      keepAlive: true, // Progress alerts should stay until manually dismissed or completed
      createdAt: Date.now(),
      duration: 0,
      progress: clampedProgress,
    };

    alerts.value.push(alert);
    return alertId;
  }

  return {
    alerts,
    showAlert,
    showAlertWithButtons,
    resolveAlert,
    hideAlert,
    hideAllAlerts,
    updateAlert,
    progress,
  };
});
